/**
 * OS1 shared device detection contract.
 *
 * Single source of truth for `apps/web` and `apps/mobile` so the install CTA
 * routes visitors to the same place from both surfaces.
 *
 * Keep this file framework-free (no Vue, no React). It must work in any
 * browser context: SPA, SSR fallback (via { userAgent } override), PWA shell,
 * Capacitor WebView.
 */

export type DeviceClass = "mobile" | "tablet" | "desktop";

export type InstallTarget = "desktop-app" | "mobile-apk" | "pwa-add-to-home";

export interface DeviceInfo {
  class: DeviceClass;
  isTouch: boolean;
  isStandalone: boolean;
  os: "android" | "ios" | "windows" | "macos" | "linux" | "unknown";
  /** What we should suggest the visitor install on this device. */
  installTarget: InstallTarget;
}

export interface DetectOptions {
  /** Override navigator.userAgent. Useful for SSR and tests. */
  userAgent?: string;
  /** Override window.matchMedia. Useful for SSR and tests. */
  matchMedia?: (query: string) => { matches: boolean };
}

function safeUserAgent(opts?: DetectOptions): string {
  if (opts?.userAgent !== undefined) return opts.userAgent;
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    return navigator.userAgent;
  }
  return "";
}

function safeMatchMedia(
  opts: DetectOptions | undefined,
  query: string,
): boolean {
  const mm =
    opts?.matchMedia ??
    (typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia.bind(window)
      : undefined);
  if (!mm) return false;
  try {
    return mm(query).matches;
  } catch {
    return false;
  }
}

function detectOs(ua: string): DeviceInfo["os"] {
  const s = ua.toLowerCase();
  if (/android/.test(s)) return "android";
  if (/iphone|ipad|ipod/.test(s)) return "ios";
  // iPadOS 13+ reports as Mac with touch
  if (/macintosh/.test(s) && /mobile/.test(s)) return "ios";
  if (/windows/.test(s)) return "windows";
  if (/mac os x|macintosh/.test(s)) return "macos";
  if (/linux/.test(s)) return "linux";
  return "unknown";
}

function detectClass(ua: string, isTouch: boolean): DeviceClass {
  const s = ua.toLowerCase();
  if (/ipad|tablet|playbook|silk/.test(s)) return "tablet";
  if (/android/.test(s) && !/mobile/.test(s)) return "tablet";
  if (/mobi|iphone|ipod|android.*mobile|windows phone/.test(s)) return "mobile";
  // iPadOS desktop UA quirk
  if (isTouch && /macintosh/.test(s)) return "tablet";
  return "desktop";
}

function pickInstallTarget(
  cls: DeviceClass,
  os: DeviceInfo["os"],
  isStandalone: boolean,
): InstallTarget {
  if (isStandalone) return "pwa-add-to-home"; // already installed, no-op surface
  if (cls === "mobile" || cls === "tablet") {
    if (os === "android") return "mobile-apk";
    return "pwa-add-to-home"; // iOS: no APK, suggest Add to Home Screen
  }
  return "desktop-app";
}

export function detectDevice(opts?: DetectOptions): DeviceInfo {
  const ua = safeUserAgent(opts);
  const isTouch =
    safeMatchMedia(opts, "(pointer: coarse)") ||
    (typeof navigator !== "undefined" &&
      (navigator.maxTouchPoints ?? 0) > 0);
  const isStandalone =
    safeMatchMedia(opts, "(display-mode: standalone)") ||
    (typeof navigator !== "undefined" &&
      // @ts-expect-error iOS-only Safari flag
      navigator.standalone === true);

  const os = detectOs(ua);
  const cls = detectClass(ua, isTouch);
  const installTarget = pickInstallTarget(cls, os, isStandalone);

  return { class: cls, isTouch, isStandalone, os, installTarget };
}

/**
 * Canonical routes the web app should use when sending visitors to the
 * install path. Keep in sync with `apps/mobile` and `apps/web` routers.
 */
export const INSTALL_ROUTES = {
  desktopApp: "/download/desktop",
  mobileApk: "/m/install",
  pwaAddToHome: "/m/install",
} as const;

export function routeForInstall(target: InstallTarget): string {
  switch (target) {
    case "desktop-app":
      return INSTALL_ROUTES.desktopApp;
    case "mobile-apk":
      return INSTALL_ROUTES.mobileApk;
    case "pwa-add-to-home":
      return INSTALL_ROUTES.pwaAddToHome;
  }
}
